<template>
  <v-container fluid justify-start>

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'User Manager'"
      v-bind:icon="'people'"
    />
    
    <!-- Herds Dialog -->
    <v-dialog
      v-model="hxHerdDialog"
      max-width="400px"
      :persistent="true"
      >
      <template v-if="herds.length === 0 && !isLoading">
        <h1 class="font-weight-light">
          No herds found
        </h1>
      </template>
      <template v-else>

        <!-- Toolbar -->
        <v-toolbar
          dark
          :color="appColor_Medium"
          >
          <v-btn
            icon
            dark
            @click="herdCancel"
            >
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title class="body-2">
            {{ editedUser.name }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              dark
              :color="appColor_Medium"
              @click="herdSelected"
              class="elevation-0"
              >
              OK
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Checkboxes -->
        <v-container
          :style="{ maxHeight: '550px', backgroundColor: 'white' }"
          class="overflow-y-auto pa-0 ma-0"
          >
          <loading
            :active.sync="isSavingHerds"
            :can-cancel="false"
            loader="dots"
            :color="appColor_Progress"
          />
          <v-card-text class="pl-4 pr-4 pt-5">
            <div
              v-for="herd in editedUserHerds"
              :key="herd.id"
              xs12
              >
              <v-checkbox
                v-model="herd.selected"
                :color="appColor_Selected"
                >
                <template v-slot:label>
                  {{ herd.name }}
                </template>
              </v-checkbox>
            </div>
          </v-card-text>
        </v-container>
      </template>
    </v-dialog>

    <!-- BB Account Dialog -->
    <v-dialog
      v-model="bbCustomerDialog"
      max-width="900px"
      :persistent="true"
      >
      <bb-customer-selector
        v-bind:isSaving="isSaving"
        @select="customerSelected"
        @cancel="customerCancel"
      />
    </v-dialog>

    <!-- HX Account Dialog -->
    <v-dialog
      v-model="hxAccountDialog"
      max-width="400px"
      :persistent="true"
      >
      <hx-account-selector
        v-bind:isSaving="isSaving"
        @select="accountSelected"
        @cancel="accountCancel"
      />
    </v-dialog>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="users"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      mobile-breakpoint="500"
      >
      <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate />

      <!-- Edit Dialog -->
      <template v-slot:top>
        <v-toolbar dark :color="appColor_Medium">
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search"
            single-line
            :disabled="isLoading"
            hide-details
            clearable
          />
          <v-spacer />

          <v-dialog
            v-model="editDialog"
            max-width="500px"
            :persistent="true"
            >
            <!-- New User Button -->
            <template v-slot:activator="{ on }">
              <v-btn
                outlined
                v-on="on"
                :small="isSmallDevice"
                :disabled="isLoading"
                class="ml-3"
                >
                New User
              </v-btn>
            </template>

            <v-card>
              
              <!-- Toolbar -->
              <v-toolbar dark flat :color="appColor_Light">
                <v-icon class="mr-2">
                  edit
                </v-icon> 
                <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                <v-spacer />
                <v-list-item-avatar v-if="editedUser.id.length > 0">
                  <v-img :src="editedUser.picture"></v-img>
                </v-list-item-avatar>
              </v-toolbar>

              <!-- Form -->
              <v-card-text>
                <v-container grid-list-md>
                  <form>
                    <v-row>
                      <!-- Email -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="8"
                        md="8"
                        >
                        <v-text-field
                          v-model="editedUser.email"
                          v-validate="'required|email'"
                          label="Email"
                          :error-messages="errors.collect('email')"
                          data-vv-name="email"
                          :disabled="editedUser.id.length > 0"
                          :clearable="editedUser.id.length === 0"
                          hide-details="auto"
                        />
                      </v-col>

                      <!-- Email Verification  -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="4"
                        md="4"
                        >
                        <v-switch
                          v-if="editedUser.id.length === 0"
                          v-model="editedUser.isEmailVerified"
                          label="Auto-verify"
                          color="success"
                          persistent-hint
                          :hint="editedUser.isEmailVerified ? 'Don\'t send an email' : 'Email the user'"
                          >
                        </v-switch>
                        <v-checkbox
                          v-else
                          v-model="editedUser.isEmailVerified"
                          label="Verified"
                          color="success"
                        />
                      </v-col>

                      <!-- Name -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="8"
                        md="8"
                        >
                        <v-text-field
                          v-model="editedUser.name"
                          v-validate="'required|max:50'"
                          label="Name"
                          :error-messages="errors.collect('name')"
                          data-vv-name="name"
                          clearable
                          hide-details="auto"
                        />
                      </v-col>

                      <!-- Nickname -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="4"
                        md="4"
                        >
                        <v-text-field
                          v-model="editedUser.nickname"
                          v-validate="'required|max:20'"
                          label="Nickname"
                          :error-messages="errors.collect('nickname')"
                          data-vv-name="nickname"
                          clearable
                          hide-details="auto"
                        />
                      </v-col>

                      <!-- Company Name -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="12"
                        >
                        <v-text-field
                          v-model="editedUser.companyName"
                          label="Company"
                          clearable
                          hide-details="auto"
                        />
                      </v-col>

                      <!-- Phone 1 -->
                      <v-col
                        cols="12"
                        sm="6"
                        >
                        <v-text-field
                          v-model="editedUser.phone1"
                          label="Phone 1"
                          prepend-icon="phone"
                          clearable
                          hide-details
                        />
                      </v-col>

                      <!-- Phone 2 -->
                      <v-col
                        cols="12"
                        sm="6"
                        >
                        <v-text-field
                          v-model="editedUser.phone2"
                          label="Phone 2"
                          prepend-icon="phone"
                          clearable
                          hide-details
                        />
                      </v-col>

                      <!-- BB Customer -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <v-checkbox
                          v-model="editedUser.isBeefboosterCustomer"
                          label="Beefbooster Customer"
                          :color="appColor_Selected"
                          hide-details
                        />
                      </v-col>

                      <!-- HX Customer -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <v-checkbox
                          v-model="editedUser.isHerdtraxCustomer"
                          label="Herdtrax Customer"
                          :color="appColor_Selected"
                          hide-details
                        />
                      </v-col>

                      <!-- Herd Size -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <number-field
                          v-model="editedUser.cowHerdSize"
                          :label="'Herd Size'"
                          :min="1"
                          :max="99999"
                          :clearable="true"
                        />
                      </v-col>

                      <!-- Role -->
                      <v-col
                        cols="12"
                        xs="12"
                        sm="6"
                        md="6"
                        >
                        <v-select
                          v-model="editedUser.role"
                          label="Role"
                          :items="roles"
                          hint="Select Role"
                          hide-details
                        />
                      </v-col>

                      <!-- Bull Preferences -->
                      <v-col
                        cols="12"
                        sm="12"
                        >
                        <v-textarea
                          v-model="editedUser.bullPreferences"
                          v-validate="'max:1000'"
                          label="Bull Preferences"
                          :error-messages="errors.collect('bullPreferences')"
                          data-vv-name="bullPreferences"
                          hide-details
                          clearable
                          dense
                        />
                      </v-col>
                    </v-row>
                  </form>
                </v-container>
              </v-card-text>

              <v-card-actions class="mr-4 pt-0">
                
                <!-- Password (add only) -->
                <template v-if="editedUser.id.length === 0">
                  <v-text-field
                    v-model="fixedPassword"
                    label="Password"
                    readonly
                    class="ml-4"
                    />
                  <v-btn
                    v-clipboard:copy="fixedPassword"
                    v-clipboard:success="onCopy"
                    icon
                    >
                    <v-icon>insert_drive_file</v-icon>
                  </v-btn>
                </template>

                <!-- UserId (edit only) -->
                <template v-else>
                  <v-text-field
                    v-model="editedUser.id"
                    label="Id"
                    disabled
                    class="caption ml-4"
                  />
                  <v-btn
                    v-clipboard:copy="editedUser.id"
                    v-clipboard:success="onCopy"
                    icon
                    >
                    <v-icon>insert_drive_file</v-icon>
                  </v-btn>
                </template>

                <v-spacer />

                <!-- Cancel -->
                <v-btn
                  :disabled="isSaving"
                  text
                  @click="closeDialog"
                  >
                  Cancel
                </v-btn>

                <!-- OK -->
                <v-btn
                  :color="appColor_ButtonOK"
                  :disabled="isSaving"
                  :loading="isSaving"
                  text
                  @click="save"
                  >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <template v-slot:item.picture="{ item }">
        <span class="ml-3">
          <v-list-item-avatar>
            <v-img :src="item.picture" />
          </v-list-item-avatar>
        </span>
      </template>

      <!-- <template v-slot:item.identities.isSocial="{ item }">
        <span class="text-left">
          <v-badge v-if="item.identities.isSocial" :content="item.identities.provider" color="blue darken-2" small inline></v-badge>
          <v-badge v-else :content="item.identities.provider" color="grey" small inline></v-badge>
        </span>
      </template> -->

      <template v-slot:item.name="{ item }">
        <span class="text-left">
          <router-link to="/userManager">
            <span @click="edit(item)">{{ item.name }}</span>
          </router-link>
        </span>
      </template>

      <!-- info popup -->
      <template v-slot:item.id="{ item }">
        <v-menu top offset-y>
          <template v-slot:activator="{ on }">
            <v-btn icon color="primary" v-on="on" class="mr-n4 elevation-2">
              <v-icon color="primary">info</v-icon>
            </v-btn>
          </template>
          <v-card max-width="200px">
            <v-list dense class="py-2">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="body-2 font-weight-bold">
                    Created
                  </v-list-item-title>
                  <div class="caption font-weight-regular">
                    {{ getDate(item.dateCreated) }}
                  </div>
                  <div class="caption font-weight-regular">
                    {{ getTime(item.dateCreated) }}
                  </div>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="body-2 font-weight-bold">
                    Updated
                  </v-list-item-title>
                  <div class="caption font-weight-regular">
                    {{ getDate(item.dateUpdated) }}
                  </div>
                  <div class="caption font-weight-regular">
                    {{ getTime(item.dateUpdated) }}
                  </div>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="body-2 font-weight-bold">
                    Last Login
                  </v-list-item-title>
                  <div class="caption font-weight-regular">
                    {{ getDate(item.dateLastLogin) }}
                  </div>
                  <div class="caption font-weight-regular">
                    {{ getTime(item.dateLastLogin) }}
                  </div>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="body-2 font-weight-bold">
                    Logins
                  </v-list-item-title>
                  <span class="caption font-weight-regular">
                    {{ item.numLogins }}
                  </span>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>

      <template v-slot:item.email="{ item }">
        <span class="text-left text-no-wrap">
          <v-badge v-if="item.isEmailVerified" inline icon="done" color="success"></v-badge>
          <v-badge v-else inline icon="close" color="error"></v-badge>
          {{ item.email }}
        </span>
      </template>

      <template v-slot:item.role="{ item }">
        <v-chip dark small :color="getRoleColor(item.role)">
          {{ item.role }}
        </v-chip>
      </template>

      <template v-slot:item.beefboosterCompanyName="{ item }">
        <template v-if="item.isBeefboosterCustomer">
          <v-btn
            v-if="item.beefboosterAccountNo === null"
            icon
            small
            outlined
            color="pink lighten-2"
            @click="chooseBBCustomer(item)"
            >
            <v-icon>priority_high</v-icon>
          </v-btn>
          <router-link v-else to="/userManager">
            <span class="text-left" @click="chooseBBCustomer(item)">
              {{ item.beefboosterCompanyName }}
            </span>
          </router-link>
        </template>
      </template>

      <template v-slot:item.herdtraxAccountId="{ item }">
        <template v-if="item.isHerdtraxCustomer">
          <v-btn
            v-if="item.herdtraxAccountId === null"
            icon
            small
            outlined
            color="pink lighten-2"
            @click="chooseHXCustomer(item)"
            >
            <v-icon>priority_high</v-icon>
          </v-btn>
          <router-link
            v-else
            to="/userManager"
            >
            <span @click="chooseHXCustomer(item)">{{ item.herdtraxAccountId }}</span>
          </router-link>
        </template>
      </template>

      <template v-slot:item.herds="{ item }">
        <span class="text-center">
          <v-btn
            icon
            @click="assignHXHerds(item)"
          >
            <v-icon>details</v-icon>
          </v-btn>
        </span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon @click="remove(item)">
          delete
        </v-icon>
      </template>
    </v-data-table>

    <!-- Clipboard Copy Snackbar-->
    <v-snackbar
      v-model="copySnackbar"
      :timeout="2000"
      >
      Copied!
      <v-btn
        dark
        text
        @click="copySnackbar = false"
      >
        Close
      </v-btn>
    </v-snackbar>

    <!-- Error Dialog -->
    <v-dialog
      v-model="errorDialog"
      max-width="400px"
      >
      <app-error
        :error-title="errorTitle"
        :error-text="errorText"
        @close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import BbCustomerSelector from '@/components/BbCustomerSelector'
import HxAccountSelector from '@/components/HxAccountSelector'
import NumberField from '@/components/NumberField'
import AppError from '@/components/AppError'
import { AppColor } from '@/config.js'

export default {
  name: 'UserManager',
  components: {
    AppPageTitle,
    AppError,
    BbCustomerSelector,
    HxAccountSelector,
    NumberField
  },
  $_veeValidate: {
    validator: 'new'
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_ButtonOK: AppColor.ButtonOK,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,

      isLoading: true,
      isLoadingHerds: true,
      isSaving: false,
      isSavingHerds: false,
      search: '',
      devInProgressDialog: false,
      editDialog: false,
      confirmDialog: false,
      bbCustomerDialog: false,
      hxAccountDialog: false,
      hxHerdDialog: false,
      users: [],
      roles: [],
      herds: [],
      editedUserHerds: [],
      fixedPassword: 'C@ttlytic$',
      copySnackbar: false,
      headers: [
        {
          value: 'picture',
          align: 'right',
          width: '2%',
          sortable: false
        },
        {
          value: 'id',
          align: 'center',
          width: '2%'
        },
        {
          text: 'Name',
          value: 'name',
          align: 'left',
          width: '20%'
        },
        {
          text: 'Email',
          value: 'email',
          align: 'left',
          width: '22%'
        },
        {
          text: 'Role',
          value: 'role',
          align: 'center',
          width: '10%'
        },
        {
          text: 'Beefbooster',
          value: 'beefboosterCompanyName',
          align: 'center',
          width: '15%'
        },
        {
          text: 'Herdtrax',
          value: 'herdtraxAccountId',
          align: 'center',
          width: '15%'
        },
        {
          text: 'Herds',
          value: 'herds',
          align: 'center',
          sortable: false,
          width: '10%'
        },
        {
          value: 'action',
          sortable: false,
          width: '5%'
        }
      ],
      editedIndex: -1,
      editedUser: {
        id: '',
        picture: '',
        name: null,
        nickname: null,
        email: null,
        companyName: null,
        phone1: null,
        phone2: null,
        cowHerdSize: null,
        bullPreferences: null,
        isBeefboosterCustomer: false,
        beefboosterAccountNo: null,
        isBeefboosterValid: false,
        isBeefboosterBreeder: false,
        beefboosterCompanyName: null,
        isHerdtraxCustomer: false,
        herdtraxAccountId: null,
        isHerdtraxValid: false,
        isHerdtraxBreeder: false,
        herdtraxHerds: null,
        role: null,
        isEmailVerified: false,
        dateLastLogin: null,
        numLogins: null
      },
      defaultUser: {
        id: '',
        picture: '',
        name: null,
        nickname: null,
        email: null,
        companyName: null,
        phone1: null,
        phone2: null,
        cowHerdSize: null,
        bullPreferences: null,
        isBeefboosterCustomer: false,
        beefboosterAccountNo: null,
        isBeefboosterValid: false,
        isBeefboosterBreeder: false,
        beefboosterCompanyName: null,
        isHerdtraxCustomer: false,
        herdtraxAccountId: null,
        isHerdtraxValid: false,
        isHerdtraxBreeder: false,
        herdtraxHerds: null,
        role: null,
        isEmailVerified: false,
        dateLastLogin: null,
        numLogins: null
      },
      dictionary: {
        attributes: {
          email: 'Email'
        },
        custom: {
          email: {
            required: () => 'Email is required'
          },
          name: {
            required: () => 'Name is required',
            max: 'Name cannot be more than 50 characters'
          },
          nickname: {
            required: () => 'Nickname is required',
            max: 'Nickname cannot be more than 20 characters'
          },
          bullPreferences: {
            max: 'Bull Preferences cannot be more than 1000 characters'
          }
        }
      },

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null
    }
  },
  computed: {
    noDataText () {
      if (this.isLoading) return 'One moment...'

      return this.users.length === 0
        ? 'No users found'
        : ''
    },
    formTitle () {
      return this.editedUser.id.length > 0 ? 'Edit User' : 'New User'
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm'
    }
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true
      self.isLoadingHerds = true
      appService.getUsers().then(data => {
        if (data.errorSeverity > 0) {
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.users = data.users
          self.roles = data.roles
          self.isLoading = false
          appService.getHerdsForSelector().then(data => {
            if (data.errorSeverity > 0) {
              self.errorText = data.errorMessage
              self.errorDialog = true
            } else {
              self.herds = data
              self.isLoadingHerds = false
            }
          })
        }
      })
    },

    edit (item) {
      this.editedIndex = this.users.indexOf(item)
      this.editedUser = Object.assign({}, item)
      this.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.users.indexOf(item)
      const name = self.users[index].name

      self.$confirm('<p class="subtitle-1 pt-5">' + name + '</p>', {
        color: 'red darken-3',
        icon: 'help',
        title: ' Delete User?',
        width: 300,
        persistent: true
      }).then(res => {
        if (res) {
          self.confirmDialog = false
          appService.deleteUser(item.id).then((data) => {
            if (data.errorSeverity > 0) {
              self.errorText = data.errorMessage
              self.errorDialog = true
            } else {
              self.users.splice(index, 1)
            }
          })
        }
      })
    },

    save () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true
          if (self.editedIndex > -1) {
            var editedUser = self.users[self.editedIndex]
            Object.assign(editedUser, self.editedUser)
            const userModel = self.getModel(editedUser)
            appService.updateUser(editedUser.id, userModel).then((data) => {
              if (data.errorSeverity > 0) {
                self.errorText = data.errorMessage
                self.errorDialog = true
              } else {
                var user = self.users[self.editedIndex]
                user.role = data.user.role
                if (!userModel.isBeefboosterCustomer) {
                  user.beefboosterAccountNo = null
                  user.isBeefboosterValid = false
                  user.beefboosterCompanyName = null
                }
                if (!userModel.isHerdtraxCustomer) {
                  user.herdtraxAccountId = null
                  user.isHerdtraxValid = false
                }
                // const user = data.user
                // self.users[self.editedIndex].beefboosterAccountNo = user.beefboosterAccountNo
                // self.users[self.editedIndex].isBeefboosterValid = user.isBeefboosterValid

                // self.users[self.editedIndex].herdtraxAccountId = user.herdtraxAccountId
                // self.users[self.editedIndex].isHerdtraxValid = user.isHerdtraxValid

                // const notificationModel = {
                //   id: editedUser.id,
                //   isBeefboosterCustomer: editedUser.isBeefboosterCustomer,
                //   isBeefboosterValid: user.isBeefboosterValid,
                //   isHerdtraxCustomer: editedUser.isHerdtraxCustomer,
                //   isHerdtraxValid: user.isHerdtraxValid,
                //   isNewUser: false
                // }
                // appService.updateUserNotifications(notificationModel).then(() => {
                //   self.closeDialog()
                //   self.isSaving = false
                // })
              }
              self.closeDialog()
              self.isSaving = false
            })
          } else {
            appService.createUser(self.getModel(self.editedUser)).then((data) => {
              if (data.errorSeverity === 0) {
                self.errorTitle = 'Validation Failed'
                self.errorText = data.errorMessage
                self.errorDialog = true
              } else if (data.errorSeverity > 0) {
                self.errorText = data.errorMessage
                self.errorDialog = true
              } else {
                self.editedUser.id = data.user.id
                self.editedUser.picture = data.user.picture
                self.editedUser.isEmailVerified = data.user.isEmailVerified
                self.editedUser.dateCreated = data.user.dateCreated
                self.editedUser.dateUpdated = data.user.dateUpdated
                self.editedUser.dateLastLogin = data.user.dateLastLogin
                self.editedUser.numLogins = data.user.numLogins
                self.editedUser.role = data.user.role
                self.users.unshift(self.editedUser)
              }
              self.isSaving = false
              self.closeDialog()
            })
          }
        }
      })
    },

    getModel (u) {
      const herdSize = u.cowHerdSize !== null
        ? parseInt(u.cowHerdSize)
        : null

      return (u.id.length > 0) 
        ? {
          email: u.email,
          nickname: this.trimText(u.nickname),
          name: this.trimText(u.name),
          companyName: this.trimText(u.companyName),
          phone1: this.trimText(u.phone1),
          phone2: this.trimText(u.phone2),
          cowHerdSize: herdSize,
          bullPreferences: this.trimText(u.bullPreferences),
          isBeefboosterCustomer: u.isBeefboosterCustomer,
          isBeefboosterValid: u.isBeefboosterValid,
          isHerdtraxCustomer: u.isHerdtraxCustomer,
          isHerdtraxValid: u.isHerdtraxValid,
          role: u.role != null ? u.role : null,
          isEmailVerified: u.isEmailVerified
        }
        : {
          email: this.trimText(u.email),
          password: this.trimText(this.fixedPassword),
          name: this.trimText(u.name),
          nickname: this.trimText(u.nickname),
          companyName: this.trimText(u.companyName),
          phone1: this.trimText(u.phone1),
          phone2: this.trimText(u.phone2),
          cowHerdSize: herdSize,
          bullPreferences: this.trimText(u.bullPreferences),
          isBeefboosterCustomer: u.isBeefboosterCustomer,
          isHerdtraxCustomer: u.isHerdtraxCustomer,
          role: u.role != null ? u.role : null,
          isEmailVerified: u.isEmailVerified
        }
    },

    trimText(text) {
      return !this.isNullOrUndefined(text)
        ? text.length > 0 ? text.trim() : null
        : null
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedUser()
      }, 300)
    },

    customerSelected ({ account: account }) {
      const self = this
      self.isSaving = true
      const editedUser = self.users[self.editedIndex]

      appService.updateBBAccountStatus(editedUser.id, account).then((data) => {
        if (data.errorSeverity > 0) {
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.users[self.editedIndex].beefboosterAccountNo = data.beefboosterAccountStatus.accountNo
          //self.users[self.editedIndex].beefboosterContactName = data.beefboosterAccountStatus.contactName
          self.users[self.editedIndex].beefboosterCompanyName = data.beefboosterAccountStatus.companyName !== null
            ? data.beefboosterAccountStatus.companyName
            : data.beefboosterAccountStatus.contactName
          self.users[self.editedIndex].isBeefboosterBreeder = data.beefboosterAccountStatus.isBreeder
          self.users[self.editedIndex].isBeefboosterValid = data.beefboosterAccountStatus.isValid
          self.users[self.editedIndex].role = data.beefboosterAccountStatus.role

          // const notificationModel = {
          //   id: editedUser.id,
          //   isBeefboosterCustomer: data.beefboosterAccountStatus.isCustomer,
          //   isBeefboosterValid: data.beefboosterAccountStatus.isValid,
          //   isHerdtraxCustomer: editedUser.isHerdtraxCustomer,
          //   isHerdtraxValid: editedUser.isHerdtraxValid,
          //   isNewUser: false
          // }
          // appService.updateUserNotifications(notificationModel).then(() => {
          //   self.closeDialog()
          //   self.isSaving = false
          //   self.bbCustomerDialog = false
          //   self.resetEditedUser()
          // })
        }
        self.isSaving = false
        self.bbCustomerDialog = false
        self.resetEditedUser()
      })
    },

    customerCancel () {
      this.bbCustomerDialog = false
      this.resetEditedUser()
    },

    chooseBBCustomer (item) {
      this.editedIndex = this.users.indexOf(item)
      this.editedUser = Object.assign({}, item)
      this.bbCustomerDialog = true
    },

    accountSelected (accountId) {
      const self = this
      self.isSaving = true
      var editedUser = self.users[self.editedIndex]
      appService.updateHXAccountStatus(editedUser.id, accountId).then((data) => {
        editedUser.herdtraxAccountId = data.herdtraxAccountStatus.accountId
        editedUser.isHerdtraxValid = data.herdtraxAccountStatus.isValid
        // const notificationModel = {
        //   id: editedUser.id,
        //   isHerdtraxCustomer: data.isCustomer,
        //   isHerdtraxValid: data.isValid,
        //   isBeefboosterCustomer: editedUser.isBeefboosterCustomer,
        //   isBeefboosterValid: editedUser.isBeefboosterValid,
        //   isNewUser: false
        // }
        // appService.updateUserNotifications(notificationModel).then(() => {
        //   self.closeDialog()
        //   self.isSaving = false
        //   self.hxAccountDialog = false
        //   self.resetEditedUser()
        //   self.$refs.hxcust.reset()
        // })
        self.closeDialog()
        self.isSaving = false
        self.hxAccountDialog = false
        self.resetEditedUser()
      })
    },

    accountCancel () {
      this.hxAccountDialog = false
      this.resetEditedUser()
    },

    herdSelected () {
      const self = this
      self.isSavingHerds = true
      const selectedHerds = self.editedUserHerds.filter(h => { return h.selected })
      const herds = selectedHerds.map(h => h.name)
      appService.updateUserHerds(self.editedUser.id, herds).then((data) => {
        if (data.errorSeverity > 0) {
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.users[self.editedIndex].herdtraxHerds = herds
          self.resetEditedUser()
        }
        self.isSavingHerds = false
        self.hxHerdDialog = false
      })
    },

    herdCancel () {
      this.hxHerdDialog = false
      this.resetEditedUser()
    },

    chooseHXCustomer (item) {
      this.editedIndex = this.users.indexOf(item)
      this.editedUser = Object.assign({}, item)
      this.hxAccountDialog = true
    },

    assignHXHerds (item) {
      this.editedIndex = this.users.indexOf(item)
      this.editedUser = Object.assign({}, item)

      const self = this
      self.editedUserHerds = self.herds.map(h => {
        return {
          name: h.name,
          selected: self.editedUser.herdtraxHerds !== null
            ? self.editedUser.herdtraxHerds.includes(h.name)
            : false
        }
      })
      self.hxHerdDialog = true
    },

    resetEditedUser () {
      this.editedUser = Object.assign({}, this.defaultUser)
      this.editedIndex = -1
      this.$validator.reset()
    },

    onCopy () {
      this.copySnackbar = true
    },

    customFilter (value, search, item) {
      const searchText = search.toLowerCase()
      const beefboosterCompanyName = item.beefboosterCompanyName !== null
        ? item.beefboosterCompanyName.toLowerCase()
        : ''

      const herdtraxAccountId = item.herdtraxAccountId !== null
        ? item.herdtraxAccountId.toLowerCase()
        : ''

      return item.name.toLowerCase().indexOf(searchText) !== -1 ||
        item.email.toLowerCase().indexOf(searchText) !== -1 ||
        beefboosterCompanyName.indexOf(searchText) !== -1 ||
        herdtraxAccountId.indexOf(searchText) !== -1
    },

    getRoleColor (role) {
      if (role === 'Admin') return 'indigo'
      if (role === 'Breeder') return 'teal'
      if (role === 'Guest') return 'grey'
    },

    getDate (date) {
      if (date !== null) {
        if (date.length > 0) {
          var dt = new Date(date)
          if (dt.getFullYear() === 1) return 'Never'
          return this.$moment(date).format('dddd, MMMM D YYYY')
        }
      }
      return ''
    },

    getTime (date) {
      if (date !== null) {
        if (date.length > 0) {
          var dt = new Date(date)
          if (dt.getFullYear() === 1) return ''
          return this.$moment(date).format('h:mm A')
        }
      }
      return ''
    },

    isNullOrUndefined (obj) {
      return obj === undefined 
        || typeof obj === 'undefined' 
        || obj === null
    }
  },
  created () {
    this.loadData()
  },
  mounted () {
    this.$validator.localize('en', this.dictionary)
  },
  watch: {
    editDialog (val) {
      val || this.closeDialog()
    }
  }
}
</script>
